/* eslint-disable */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { AiLabContext } from 'builders/builderInternal/context/AiLabContext';
import { Button } from 'shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from 'shared/components/ui/dialog';
import { Clock, User, InfoIcon, Bot, X } from 'lucide-react';
import { ScrollArea, ScrollBar } from 'shared/components/ui/scroll-area';
import { Label } from 'shared/components/ui/label';
import { Badge } from 'shared/components/ui/badge';
import { FilterBar, SearchBar } from 'builders/builderInternal/components/Instructions/Filters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'shared/components/ui/tooltip';

function ResultsModal({ results, onClose, anchorRef }) {
    const modalRef = useRef();
    const [modalStyle, setModalStyle] = useState({});
    const [ready, setReady] = useState(false);

    useEffect(() => {
        setReady(false);
    }, [results, anchorRef]);

    useEffect(() => {
        if (anchorRef.current && modalRef.current) {
            const cardRect = anchorRef.current.getBoundingClientRect();
            const scrollY = window.scrollY || document.documentElement.scrollTop;
            const modalWidth = cardRect.width;
            const modalHeight = modalRef.current.offsetHeight;

            setModalStyle({
                position: "absolute",
                left: `${cardRect.left / 2 - 130}px`,
                top: `${cardRect.top + scrollY + cardRect.height / 2 - 130}px`,
                width: `${cardRect.width}px`,
                minWidth: "350px",
                zIndex: 1000,
                visibility: "visible",
                pointerEvents: "auto",
            });
            setReady(true);
        }
    }, [anchorRef, results]);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(30,41,59,0.16)]">
            <div
                ref={modalRef}
                style={{
                    ...modalStyle,
                    // Hide the modal until measured and positioned
                    visibility: ready ? "visible" : "hidden",
                    pointerEvents: ready ? "auto" : "none",
                }}
                className="relative bg-card text-card-foreground rounded-2xl shadow-2xl mx-auto"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 rounded-full hover:bg-muted transition p-1"
                    aria-label="Close"
                    style={{ lineHeight: "1" }}
                >
                    <X size={28} />
                </button>
                <div className="px-10 pt-10 pb-8">
                    <div className="mb-5">
                        <h2 className="text-2xl font-bold mb-1">V&amp;V Results</h2>
                        <div className="text-muted-foreground text-base">Test run summary</div>
                    </div>
                    <div className="overflow-x-auto rounded-lg">
                        <table className="min-w-full border border-border bg-card rounded" style={{ fontSize: "1.09rem" }}>
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border-b border-border bg-muted font-semibold">Test Input</th>
                                    <th className="py-2 px-4 border-b border-border bg-muted font-semibold">Expected Output</th>
                                    <th className="py-2 px-4 border-b border-border bg-muted font-semibold">Similarity</th>
                                    <th className="py-2 px-4 border-b border-border bg-muted font-semibold">Timestamp</th>
                                    <th className="py-2 px-4 border-b border-border bg-muted font-semibold">User</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r, idx) => (
                                    <tr key={idx} className="hover:bg-muted/50">
                                        <td className="py-2 px-4 border-b border-border">{r.input || '-'}</td>
                                        <td className="py-2 px-4 border-b border-border">{r.expected || '-'}</td>
                                        <td className="py-2 px-4 border-b border-border">{r.similarity}</td>
                                        <td className="py-2 px-4 border-b border-border">{new Date(r.timestamp).toLocaleString()}</td>
                                        <td className="py-2 px-4 border-b border-border">{r.user}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
            `}</style>
        </div>
    );
}

export default function InstructionVnV() {
    const [testInputs, setTestInputs] = useState([""]);
    const [expectedOutputs, setExpectedOutputs] = useState([""]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedInstruction, setSelectedInstruction] = useState(null);
    const [instructionDetails, setInstructionDetails] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectType, setSelectType] = useState("All");
    const [results, setResults] = useState(null);
    const [history, setHistory] = useState([]);
    const [showResultsModal, setShowResultsModal] = useState(false);

    const cardRef = useRef();

    const { instructions, fetchInstructions, isLoadingInstructions } =
        useContext(AiLabContext);

    useEffect(() => { fetchInstructions(); }, []);
    useEffect(() => { if (isOpen) fetchInstructions(); }, [isOpen]);

    const handleInstructionSelect = (instruction) => {
        if (selectedInstruction === instruction.unique_name) {
            setSelectedInstruction(null);
            setInstructionDetails(null);
        } else {
            setSelectedInstruction(instruction.unique_name);
            setInstructionDetails(instruction);
        }
    };
    const handleSelectInstruction = () => {
        if (!selectedInstruction) return;
        const selectedInstructionDetails = instructions.find(i => i.unique_name === selectedInstruction);
        if (selectedInstructionDetails && selectedInstructionDetails.isModelProviderExist === false) return;
        setIsOpen(false);
    };
    const handleFilterModels = () => {
        if (!Array.isArray(instructions)) return [];
        return instructions.filter(instruction =>
            (instruction.unique_name?.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (selectType === 'All' || instruction.model_type === selectType)
        );
    };
    const handleInputChange = (idx, arrSetter) => e => {
        arrSetter(arr => arr.map((v, i) => i === idx ? e.target.value : v));
    };
    const handleAddField = arrSetter => () => arrSetter(arr => [...arr, ""]);
    const handleRemoveField = (idx, arrSetter) => () => arrSetter(arr => arr.filter((_, i) => i !== idx));

    const runTests = async () => {
        if (!selectedInstruction) return;
        if (!testInputs.length || !expectedOutputs.length || testInputs.some(s => !s) || expectedOutputs.some(s => !s)) return;
        const mockResults = testInputs.map((input, idx) => ({
            input,
            expected: expectedOutputs[idx] || "",
            similarity: Math.random().toFixed(2),
            timestamp: new Date().toISOString(),
            user: "Arshiya-shafizade-coolriots",
        }));
        setResults(mockResults);
        setShowResultsModal(true);
        setHistory(prev => [...prev, ...mockResults]);
    };

    const saveTestVectors = () => {
        try {
            localStorage.setItem("ai_lab_vnv_testInputs", JSON.stringify(testInputs));
            localStorage.setItem("ai_lab_vnv_expectedOutputs", JSON.stringify(expectedOutputs));
        } catch (err) { }
    };
    const loadTestVectors = () => {
        try {
            const inputs = JSON.parse(localStorage.getItem("ai_lab_vnv_testInputs") || "[]");
            const outputs = JSON.parse(localStorage.getItem("ai_lab_vnv_expectedOutputs") || "[]");
            setTestInputs(inputs.length ? inputs : [""]);
            setExpectedOutputs(outputs.length ? outputs : [""]);
        } catch (err) { }
    };

    return (
        <div className="relative w-full min-h-screen bg-background text-foreground overflow-auto">
            {/* Results Modal */}
            {showResultsModal && results && (
                <ResultsModal results={results} onClose={() => setShowResultsModal(false)} anchorRef={cardRef} />
            )}

            <div className="flex justify-center items-start w-full min-h-screen">
                <Card ref={cardRef} className="w-full max-w-3xl mt-20 mx-auto p-8 shadow-sm">
                    <CardContent>
                        <h2 className="text-2xl font-bold mb-6">AI Instruction V&V</h2>
                        <div className="mb-4">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <label className="block font-semibold mb-2">Select Instruction</label>
                            </div>
                            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                                <DialogTrigger asChild className="w-full">
                                    <Button variant="outline" className="text-left overflow-hidden w-full">
                                        {selectedInstruction || `Select an Instruction`}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh]">
                                    <DialogHeader>
                                        <DialogTitle>Select an Instruction</DialogTitle>
                                        <DialogDescription>
                                            Choose an instruction and view its details.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div className="flex items-center">
                                            <div className="flex flex-col w-full sm:flex-row gap-4 items-center p-4">
                                                <div className="flex-grow">
                                                    <SearchBar onSearch={(value) => setSearchTerm(value)} />
                                                </div>
                                                <FilterBar
                                                    onSearch={(value) => setSelectType(value)}
                                                    selectModelType={selectType}
                                                />
                                            </div>
                                            <Button onClick={fetchInstructions} className="flex gap-2" type="button" disabled={isLoadingInstructions}>
                                                {isLoadingInstructions ? "Loading Instructions..." : "Reload Instructions"}
                                            </Button>
                                        </div>
                                        <div className="flex flex-col md:flex-row gap-4">
                                            {isLoadingInstructions ? (
                                                <div className="flex items-center justify-center w-full text-muted-foreground">
                                                    <span>Loading instructions...</span>
                                                </div>
                                            ) : handleFilterModels().length === 0 ? (
                                                <div className="flex items-center justify-center w-full text-muted-foreground">
                                                    <span>No instruction(s) saved.</span>
                                                </div>
                                            ) : null}
                                            <ScrollArea className="h-[400px] flex-grow">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4 p-4">
                                                    {handleFilterModels().map((instruction) => (
                                                        <Button
                                                            key={instruction?._id || instruction.unique_name}
                                                            variant={selectedInstruction === instruction.unique_name ? "default" : "outline"}
                                                            onClick={() => handleInstructionSelect(instruction)}
                                                            className={`justify-start h-auto text-left ${instruction?.isModelProviderExist === false ? 'bg-amber-100 hover:bg-amber-50 text-amber-700 border-amber-300' : ''}`}
                                                        >
                                                            <div className="w-full py-2">
                                                                <div className='flex w-full justify-between gap-2 items-center'>
                                                                    <div className="font-semibold w-full truncate flex gap-1 items-center">
                                                                        {instruction?.isModelProviderExist === false && (
                                                                            <TooltipProvider>
                                                                                <Tooltip>
                                                                                    <TooltipTrigger asChild>
                                                                                        <div className="flex items-center">
                                                                                            <InfoIcon className="h-4 w-4 text-amber-500" />
                                                                                        </div>
                                                                                    </TooltipTrigger>
                                                                                    <TooltipContent className="max-w-xs">
                                                                                        <p> The model '{instruction.modelId}' is deprecated </p>
                                                                                    </TooltipContent>
                                                                                </Tooltip>
                                                                            </TooltipProvider>
                                                                        )}
                                                                        {instruction.unique_name}
                                                                    </div>
                                                                    <Badge className="px-1 rounded text-green-50">{instruction?.model_type}</Badge>
                                                                </div>
                                                            </div>
                                                        </Button>
                                                    ))}
                                                </div>
                                            </ScrollArea>
                                            {instructionDetails && (
                                                <ScrollArea className="max-h-[400px] min-w-[480px] max-w-[480px] pr-4">
                                                    <div className="w-full my-2">
                                                        <CardHeader>
                                                            <div className="space-y-1">
                                                                <CardTitle className="text-xl whitespace-pre-wrap break-all">{instructionDetails.unique_name}</CardTitle>
                                                                <CardDescription className="text-xs">Instruction</CardDescription>
                                                            </div>
                                                        </CardHeader>
                                                        <div className="space-y-2.5">
                                                            <div className="space-y-2">
                                                                <div className="flex items-center gap-2 text-xs">
                                                                    <User className="h-4 w-4" />
                                                                    <Label className="font-medium text-xs">Created By:</Label>
                                                                    <span className="text-muted-foreground">{instructionDetails.user_id || "-"}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2 text-xs">
                                                                    <Clock className="h-4 w-4" />
                                                                    <Label className="font-medium text-xs">Modified By:</Label>
                                                                    <span className="text-muted-foreground">{instructionDetails.modified_by || "-"}</span>
                                                                </div>
                                                            </div>
                                                            <hr />
                                                            <div className="space-y-2">
                                                                <Label className="text-xs font-medium">Description</Label>
                                                                <p className="text-xs text-muted-foreground">{instructionDetails.modelDescription || "-"}</p>
                                                                <Label className="text-xs font-medium">Prompt</Label>
                                                                <p className="text-xs text-muted-foreground whitespace-pre-wrap break-all">{instructionDetails.prompt || "-"}</p>
                                                            </div>
                                                            <hr />
                                                            <div className="space-y-2">
                                                                <div className="flex items-center gap-2">
                                                                    <Bot className="h-4 w-4" />
                                                                    <Label className="text-xs font-medium">Model ID</Label>
                                                                </div>
                                                                <code className="rounded bg-muted px-2 py-1 text-xs whitespace-pre-wrap break-all">{instructionDetails.modelId}</code>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ScrollBar orientation="horizontal" />
                                                </ScrollArea>
                                            )}
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <DialogClose asChild>
                                            <Button type="button" variant="secondary">Close</Button>
                                        </DialogClose>
                                        {selectedInstruction &&
                                            <Button variant="default" onClick={handleSelectInstruction}>
                                                Select Instruction
                                            </Button>
                                        }
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            {/* Test Inputs */}
                            <div>
                                <h3 className="font-semibold mb-2">Test Inputs <span className="text-destructive">*</span></h3>
                                {testInputs.map((input, idx) => (
                                    <div key={idx} className="flex gap-2 mb-1">
                                        <textarea
                                            value={input}
                                            onChange={handleInputChange(idx, setTestInputs)}
                                            className="flex-1 border border-input bg-background p-2 rounded resize-y min-h-[48px]"
                                            placeholder={`Test input #${idx + 1}`}
                                        />
                                        <Button
                                            onClick={handleRemoveField(idx, setTestInputs)}
                                            disabled={testInputs.length === 1}
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            aria-label="Remove"
                                        >
                                            ×
                                        </Button>
                                    </div>
                                ))}
                                <Button onClick={handleAddField(setTestInputs)} type="button" size="sm" variant="outline">Add Input</Button>
                            </div>
                            {/* Expected Outputs */}
                            <div>
                                <h3 className="font-semibold mb-2">Expected Results <span className="text-destructive">*</span></h3>
                                {expectedOutputs.map((output, idx) => (
                                    <div key={idx} className="flex gap-2 mb-1">
                                        <textarea
                                            value={output}
                                            onChange={handleInputChange(idx, setExpectedOutputs)}
                                            className="flex-1 border border-input bg-background p-2 rounded resize-y min-h-[48px]"
                                            placeholder={`Expected output #${idx + 1}`}
                                        />
                                        <Button
                                            onClick={handleRemoveField(idx, setExpectedOutputs)}
                                            disabled={expectedOutputs.length === 1}
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            aria-label="Remove"
                                        >
                                            ×
                                        </Button>
                                    </div>
                                ))}
                                <Button onClick={handleAddField(setExpectedOutputs)} type="button" size="sm" variant="outline">Add Output</Button>
                            </div>
                        </div>
                        <div className="mb-6 flex flex-wrap gap-4">
                            <Button
                                onClick={runTests}
                                disabled={
                                    !selectedInstruction ||
                                    !testInputs.length ||
                                    !expectedOutputs.length ||
                                    testInputs.some(s => !s) ||
                                    expectedOutputs.some(s => !s)
                                }
                            >
                                Run V&V Tests
                            </Button>
                            <Button onClick={saveTestVectors}>
                                Save Test Vectors
                            </Button>
                            <Button onClick={loadTestVectors}>
                                Load Test Vectors
                            </Button>
                        </div>
                        {history.length > 0 && (
                            <div className="mt-8">
                                <h4 className="font-bold mb-2">History</h4>
                                <ul className="text-xs">
                                    {history.map((r, i) =>
                                        <li key={i}>
                                            [{new Date(r.timestamp).toLocaleString()}] {r.user} ran test with similarity {r.similarity}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}   